import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/** @type {Set<DialogComponent>} */
const scrollLockHolders = new Set();

let lockedScrollY = 0;

/**
 * @param {DialogComponent} holder
 * @param {number} scrollY
 */
function acquireScrollLock(holder, scrollY) {
  const first = scrollLockHolders.size === 0;
  scrollLockHolders.add(holder);
  if (!first) return;

  lockedScrollY = scrollY;
  document.body.style.width = '100%';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
}

/** @param {DialogComponent} holder */
function releaseScrollLock(holder) {
  if (!scrollLockHolders.delete(holder)) return;
  if (scrollLockHolders.size > 0) return;

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
}

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#releaseScrollLockOnRemoval();
  }

  #releaseScrollLockOnRemoval() {
    const dialog = this.querySelector('dialog');

    if (!dialog?.open) return;
    if (!scrollLockHolders.has(this)) return;

    requestAnimationFrame(() => {
      if (this.isConnected) return;
      releaseScrollLock(this);
    });
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;

    requestAnimationFrame(() => {
      acquireScrollLock(this, scrollY);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.style.animation = 'none';

    void dialog.offsetWidth;

    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    releaseScrollLock(this);

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /** @param {MouseEvent} event - The mouse event. */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /** @param {KeyboardEvent} event - The keyboard event. */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    this.closeDialog();
  }

  /** @returns {number} The minimum width of the dialog. */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /** @returns {number} The maximum width of the dialog. */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
